/* eslint-disable no-unused-vars */
/* eslint-disable no-loop-func */
import React, {useEffect, useRef, useState} from "react";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {useHistory, useLocation} from "react-router-dom";
import {Col, Form, Row, Spinner} from "react-bootstrap";
import {FmlxButton, FmlxIcon, FmlxModal} from "fmlx-common-ui";
import Questionnaire from "../../components/Questionnaire/Questionnaire";
import {API_EMPLOYEE_REVIEW_SAVE_REVIEW} from "../../const/Api";
import {ALERT_VARIANT, EQuestionCategory, NotQualifiedMessage} from "../../const/dataEnum";
import {Post} from "../../lib/Request";
import {setAlert} from "../../stores/actions/alertModalStateAction";
import FmlxStepper from "../../components/FmlxStepper/FmlxStepper";
import {ReviewQuestionContext} from "../../lib/contexLib";
import {FMLX_BUTTON, FMLX_COMPONENT} from "../../const/FmlxComponentEnum";
import {getConfig, sessionExpired} from "../../stores/business/appBusiness";
import {isReviewCompleted, saveReview} from "../../stores/business/reviewTaskBusiness";
import employeeReviewApi from "../../api/rest/employeeReviewApi";
import {setIsSavingReview} from "../../stores/actions/appStateAction";
import ExitModal from "../../components/ExitModal";
import GeneralDescriptionContent from "../../api/rest/GeneralQuestionDescriptionApi";
import {
  manageEmployeeSelector,
  modalCreateUpdateEmployeeStateSelector,
} from "../../stores/selectors/manageEmployeeStateSelector";

export default function ReviewQuestion() {
  const locationState = useLocation().state;
  const {isSavingReviewState} = useSelector(manageEmployeeSelector, shallowEqual);
  const {userDeptId} = useSelector(modalCreateUpdateEmployeeStateSelector);
  const departmentId = locationState?.departmentId;
  const dispatch = useDispatch();
  const history = useHistory();
  const myRef = useRef(null);

  const [reviewId, setReviewId] = useState(locationState?.reviewId);
  const [readOnly, setReadOnly] = useState(locationState?.locked);
  const [myReview, setMyReview] = useState({});
  const [alreadySubmit, setSubmit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [open, setOpen] = useState(false);
  const [openJeremyNote, setOpenJeremyNote] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [notes, setNotes] = useState("");
  const [configState, setConfigState] = useState(null);
  const firstRender = useRef(true);
  const modalContent = `
    Please take time to write a thoughtful review of your subordinate, peer, or manager. 
    Note that a score of 3 means the employee met the expected performance for their position. 
    A review of all 3s means the employee is doing a good job. 
    Please do not grade significantly above 3 unless the employee performed above and beyond what was expected in the given category. 
    Also, note that only positive feedback doesn't help an employee improve. 
    Please be direct and give honest feedback about how an employee could improve their performance in the future. 
    I myself particularly invite critical feedback.
  `;
  const [changedQuestionIds, setChangedQuestionIds] = useState({
    employeeGenericReviewIds: [],
    employeeSkillReviewIds: [],
  });

  useEffect(() => {
    (async () => {
      const config = await getConfig(dispatch);
      setConfigState(config);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const resetChangedQuestions = () => {
    setChangedQuestionIds({
      employeeGenericReviewIds: [],
      employeeSkillReviewIds: [],
    });
  };

  const performSave = async (options = { resetChanges: false }) => {
    if (changedQuestionIds.employeeGenericReviewIds.length === 0 && changedQuestionIds.employeeSkillReviewIds.length === 0) {
      return;
    }
    
    dispatch(setIsSavingReview(true));
    await saveReview(dispatch, {steps, myReview, changedQuestionIds});
    
    if (options.resetChanges) {
      resetChangedQuestions();
    }
    
    dispatch(setIsSavingReview(false));
  };
  
  useEffect(() => {
    if (
      configState === null ||
      steps.length === 0 ||
      (changedQuestionIds.employeeGenericReviewIds.length === 0 &&
        changedQuestionIds.employeeSkillReviewIds.length === 0) ||
      firstRender.current
    ) {
      if (firstRender.current) {
        firstRender.current = false;
      }
      return;
    }
    let timer = null;

    timer = setTimeout(() => performSave({ resetChanges: false }), configState.autoSaveTimer);
    
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    configState,
    steps,
    myReview,
    changedQuestionIds.employeeGenericReviewIds,
    changedQuestionIds.employeeSkillReviewIds,
  ]);
  

  useEffect(() => {
    if (reviewId) {
      populateData();
    } else {
      history.goBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchGeneralDescription = async (deptId, periodId) => {
    try {
      const {data} = await GeneralDescriptionContent.getDescContentByDepartementId(deptId, periodId);
      if (!data?.length) {
        throw new Error("No description content found");
      }
      const lastIndex = data.length - 1;
      const item = data[lastIndex];
      const generalDesc = item?.description ?? "";
      setNotes(generalDesc);
    } catch (error) {
      console.error("Error fetching general description:", error);
      setNotes("");
    }
  };

  async function populateData() {
    setLoading(true);
    var success = false;
    const data = await employeeReviewApi.getQuestionnareByReviewIdAndDepartmentId(reviewId, departmentId);
    if (data) {
      try {
        await fetchGeneralDescription(departmentId, data[0]?.reviewPeriodID);
      } catch (error) {
        console.error("Error fetching general description:", error);
        setNotes("");
      }

      setMyReview(data[0]);
      let tempQ = data[0].employeeGenericReviews;
      let generalQ = [];
      tempQ.forEach((el) => {
        let dataTemp = {
          // data: el,
          employeeReviewsID: el.employeeReviewsID,
          employeeReviews: el.employeeReviews,
          id: el.id,
          questionID: el.genericReviewQuestion.id,
          category: EQuestionCategory.GENERAL,
          question: el.question,
          description: el.genericReviewQuestion?.description,
          questionType: el.genericReviewQuestion?.questionType,
          required: el.genericReviewQuestion?.required,
          score: el.score,
          comments: el.comments,
          commentsRequired: el.genericReviewQuestion?.commentsRequired,
          forManager: el.genericReviewQuestion?.forManager,
          forPeer: el.genericReviewQuestion?.forPeer,
          forSelf: el.genericReviewQuestion?.forSelf,
          orderNumber: el.skill?.orderNumber,
          isNotQualified: el.score === null && el.comments === NotQualifiedMessage,
          minimumofWords: el.genericReviewQuestion?.minimumofWords,
        };
        generalQ.push(dataTemp);
      });

      // generalQ = generalQ.sort((a, b) =>
      //   a?.orderNumber > b?.orderNumber ? 1 : a?.orderNumber < b?.orderNumber ? -1 : 0
      // );
      let general = [
        {
          title: "General Question",
          // description: generalQuestionNotes,
          description: notes,
          question: generalQ,
        },
      ];
      // setReadOnly(result.data[0].locked);
      // setGenericReview(general);
      let skillQ = populateSkillReview(data[0].employeeSkillReviews);
      // setSkillReview(skillQ);
      populateStep(general, skillQ);
      if (data[0].reviewDate === null) {
        setOpenJeremyNote(true);
      }
      success = true;
    }

    setLoading(false);
    if (!success) {
      history.goBack();
    }
  }

  function populateSkillReview(skillReviews) {
     // if (!readOnly) {
    var group = [];
    var descriptions = [];
    var gr = [];
    skillReviews.forEach((element) => {
      if (
        element &&
        element.skill &&
        element.skill.skillCategory &&
        !group.includes(element.skill.skillCategory.category)
      ) {
        group.push(element?.skill?.skillCategory?.category);
        descriptions.push(element?.skill?.skillCategory?.description);
      }
    });
    for (let i = 0; i < group.length; i++) {
      const element = group[i];
      const description = descriptions[i];
      var skill = [];
      skillReviews.forEach((el) => {
        if (el?.skill?.skillCategory?.category === element) {
          let dataTemp = {
            // data: el,
            employeeReviewsID: el.employeeReviewsID,
            employeeReviews: el.employeeReviews,
            id: el.id,
            questionID: el.skill?.id,
            category: EQuestionCategory.SKILL,
            question: el.skill?.name,
            description: el.skill?.description,
            questionType: el.skill?.skillType,
            required: el.skill?.required,
            score: el.score,
            comments: el.comments,
            commentsRequired: el.skill?.commentsRequired,
            forManager: el.skill?.forManager,
            forPeer: el.skill?.forPeer,
            forSelf: el.skill?.forSelf,
            orderNumber: el.skill?.orderNumber,
            isNotQualified: el.score === null && el.comments === NotQualifiedMessage,
            minimumofWords: el.skill?.minimumofWords,
          };
          skill.push(dataTemp);
        }
      });
      skill = skill.sort((a, b) => (a?.orderNumber > b?.orderNumber ? 1 : a?.orderNumber < b?.orderNumber ? -1 : 0));
      gr.push({
        title: element,
        description: description,
        question: skill,
      });
    }
    return gr;
  }

  function populateStep(generalQ, skillQ) {
    let id = 0;
    let tempStep = [
      {
        id: id,
        stepper: {
          id,
          title: generalQ[0].title,
          // desc: "",
          disabled: false,
          error: false,
          isCompleted: isReviewCompleted(true, generalQ[0].question),
        },
        content: generalQ[0],
        nextStepCallback: () => {
          setActiveStep((prevState) => prevState + 1);
        },
        backStepCallback: () => {},
      },
    ];
    skillQ?.forEach((el, i) => {
      id++;
      let dataTemp = {
        id: id,
        stepper: {
          id,
          title: el.title,
          // desc: "",
          disabled: false,
          error: false,
          isCompleted: isReviewCompleted(false, el.question),
        },
        content: el,
        nextStepCallback: () => {
          if (i === skillQ.length - 1) setActiveStep((prevState) => prevState + 1);
        },
        backStepCallback: () => {
          setActiveStep((prevState) => prevState - 1);
        },
      };

      tempStep.push(dataTemp);
    });
    console.log(tempStep);
    setSteps(tempStep);
  }
   // function checkSkill() {
  //   for (let skillCategory of skillReviews) {
  //     for (let skill of skillCategory.data) {
  //       if (!skill.score && !skill.comments) {
  //         dispatch(
  //           setAlert({
  //             show: true,
  //             variant: ALERT_VARIANT.ERROR,
  //             message: "Please complete the review",
  //           })
  //         );
  //         return;
  //       }
  //     }
  //   }
  //   submit().then();
  // }

  async function submit() {
    if (!readOnly) {
      setLoadingSubmit(true);
      var generic = [];
      var skill = [];
      steps.forEach((element, i) => {
        if (i === 0) {
          element.content.question.forEach((question, i) => {
            generic.push({
              genericReviewQuestionID: question.questionID,
              score: question.score,
              comments: question.comments,
            });
          });
        } else {
          element.content.question.forEach((question, i) => {
            skill.push({
              skillID: question.questionID,
              score: question.score,
              comments: question.comments,
            });
          });
        }
      });
      let param = {
        employeeReviewsID: myReview.id,
        reviewedByID: myReview.reviewedByID,
        employeeReviewedID: myReview.employeeReviewedID,
        reviewPeriodID: myReview.reviewPeriodID,
        reviewTypeID: myReview.reviewTypeID,
        projectID: myReview.projectID,
        employeeSkillReviews: skill,
        employeeGenericReviews: generic,
      };
      let responseSubmitReview = await Post(API_EMPLOYEE_REVIEW_SAVE_REVIEW, param);
      setLoadingSubmit(false);
      if (responseSubmitReview.responseCode === 0) {
        // window.removeEventListener("beforeunload", keepOnPage);
        // window.removeEventListener("popstate", onBackButtonEvent);
        dispatch(
          setAlert({
            show: true,
            variant: ALERT_VARIANT.SUCCESS,
            message: `Review for ${myReview?.reviewedEmployee?.fullName} has been saved`,
          })
        );
        history.goBack();
      } else if (responseSubmitReview.responseCode === 401) {
        sessionExpired(dispatch);
      } else {
        dispatch(
          setAlert({
            show: true,
            variant: ALERT_VARIANT.ERROR,
            message: responseSubmitReview.message,
          })
        );
      }
    }
  }
  
  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);
  };
  useEffect(() => {
    myRef?.current?.scrollIntoView();
    // window.scrollTo({
    //   top: 0,
    //   left: 0,
    //   behavior: "smooth",
    // });
  }, [activeStep]);

  const stepperItems = steps.map((step) => ({...step.stepper}));

  const isLastStep = () => {
    if (steps.length === 0) return false;
    const lastStepId = steps[steps.length - 1].id;
    return activeStep === lastStepId;
  };
  
  const handleManualSave = () => {
    performSave({ resetChanges: true });
  };
  
  const onFinishClickHandler = async () => {
    await performSave({ resetChanges: true });
    history.goBack();
  };

  const onExitModalConfirmHandler = () => {
    dispatch(setIsSavingReview(false));
  };

  const hasUnsavedChanges =
    changedQuestionIds.employeeGenericReviewIds.length > 0 ||
    changedQuestionIds.employeeSkillReviewIds.length > 0;

  return (
    <div className="container">
      <ExitModal hasUnsavedChanges={isSavingReviewState} onConfirm={onExitModalConfirmHandler} />
      {!readOnly && (
        <FmlxModal
          open={openJeremyNote}
          onCloseClick={() => {
            setOpenJeremyNote(false);
          }}
          primaryButton={{
            show: true,
            disabled: false,
            label: "close",
            type: FMLX_BUTTON.TYPE.PRIMARY,
            variant: FMLX_BUTTON.VARIANT.CONTAIN,
            onClick: () => {
              setOpenJeremyNote(false);
            },
          }}
          secondaryButton={{
            show: false,
          }}
          tertiaryButton={{
            show: false,
          }}
          title={
            <>
              <span style={{marginLeft: 0 + "px", color: "red"}}>Important!</span> Read this note from Jeremy
            </>
          }
        >
          <div style={{paddingBottom: "20px", fontSize: "16px", fontStyle: "italic"}}>
            {/* <Alert style={{ fontWeight: "bold", fontSize: "15px", padding: 13 + "px" }}>
            <div
              style={{
                textAlign: "justify",
                fontSize: "13px",
                color: "black",
                whiteSpace: "pre-wrap",
                backgroundColor: "transparent",
                fontWeight: "bold",
              }}
            > */}
            {modalContent}
            {/* </div>
          </Alert> */}          
          </div>
        </FmlxModal>
      )}
      <FmlxModal
        open={open}
        onCloseClick={() => {
          setOpen(false);
        }}
        primaryButton={{
          show: true,
          disabled: false,
          label: "SAVE",
          type: FMLX_BUTTON.TYPE.PRIMARY,
          variant: FMLX_BUTTON.VARIANT.CONTAIN,
          onClick: () => {
            setOpen(false);
            submit();
          },
        }}
        secondaryButton={{
          show: true,
          disabled: false,
          label: "CANCEL",
          type: FMLX_BUTTON.TYPE.BASIC,
          variant: FMLX_BUTTON.VARIANT.OUTLINE,
          onClick: () => {
            setOpen(false);
          },
        }}
        tertiaryButton={{
          show: false,
        }}
        title={"SAVE REVIEW"}
      >
        <div style={{paddingBottom: "20px"}}>Do you want to save your current review progress?</div>
      </FmlxModal>
      <ReviewQuestionContext.Provider
        value={{
          // genericReviews,
          // setGenericReview,
          // skillReviews,
          // setSkillReview,
          steps,
          setSteps,
          activeStep,
          setActiveStep,
          readOnly,
          setReadOnly,
          alreadySubmit,
          setSubmit,
          changedQuestionIds,
          setChangedQuestionIds,
        }}
      >
        {/* <FmlxBackdropLoading show={loadingSubmit} description="Submit" showButton={false} /> */}
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <div className="review-question--content">
            <div className="review-question--question">
              <div className="reviewTask-stepper">
                <FmlxButton
                  size={FMLX_COMPONENT.SIZE.L}
                  type={FMLX_BUTTON.TYPE.POSITIVE}
                  variant={FMLX_BUTTON.VARIANT.OUTLINE}
                  label=""
                  disabled={activeStep === 0}
                  onClick={() => {
                    if (activeStep > 0) {
                      setActiveStep(activeStep - 1);
                    }
                  }}
                  onlyIcon={true}
                  icon={<FmlxIcon name={"ArrowLeftS"} size={FMLX_COMPONENT.SIZE.M} />}
                  withTooltip={false}
                  // tooltipPlacement={"top"}
                  // tooltipDescription={"Prev"}
                ></FmlxButton>
                <FmlxStepper items={stepperItems} activeStep={activeStep} onClick={handleStepClick} />
                <FmlxButton
                  size={FMLX_COMPONENT.SIZE.L}
                  type={FMLX_BUTTON.TYPE.POSITIVE}
                  variant={FMLX_BUTTON.VARIANT.OUTLINE}
                  label=""
                  disabled={activeStep === steps.length - 1}
                  onClick={() => {
                    if (activeStep < steps.length - 1) {
                      setActiveStep(activeStep + 1);
                    }
                  }}
                  onlyIcon={true}
                  icon={<FmlxIcon name={"ArrowRightS"} size={FMLX_COMPONENT.SIZE.M} />}
                  withTooltip={false}
                  // tooltipPlacement={"top"}
                  // tooltipDescription={"Next"}
                ></FmlxButton>
                
                {readOnly ? (
                  <></>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "80px",
                      height: "40px",
                      marginLeft: "8px",
                    }}
                  >
                    <FmlxButton
                      size={FMLX_COMPONENT.SIZE.L}
                      type={FMLX_BUTTON.TYPE.PRIMARY}
                      variant={FMLX_BUTTON.VARIANT.CONTAIN}
                      label={isLastStep() ? "FINISH" : "SAVE"}
                      onClick={isLastStep() ? onFinishClickHandler : handleManualSave}
                      showProgress={isSavingReviewState}
                      progressPosition={"left"}
                      disabled={isSavingReviewState || (isLastStep() ? false : !hasUnsavedChanges)}
                    />
                  </div>
                )}
              </div>
              {steps.map((data, i) => {
                if (i === activeStep) {
                  return (
                    <React.Fragment key={i}>
                      <Questionnaire
                        title={data.content.title}
                        notes={data.content.description || notes}
                        question={data.content.question}
                      />
                    </React.Fragment>
                  );
                } else {
                  return <React.Fragment key={i} />;
                }
              })}
            </div>
            <div ref={myRef}></div>
            <div className="review-question--review-detail">
              <Row className="">
                <Col md={12} sm={12}>
                  <Form.Group size="lg" className="mb-2">
                    <Form.Label>Reviewer</Form.Label>
                    <Form.Control type="text" placeholder="Reviewer" value={myReview?.reviewedBy?.fullName} readOnly />
                  </Form.Group>
                </Col>
                <Col md={12} sm={12}>
                  <Form.Group size="lg" className="mb-2">
                    <Form.Label>Review For</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Review For"
                      value={myReview?.reviewedEmployee?.fullName}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={12} sm={12}>
                  <Form.Group size="lg" className="mb-2">
                    <Form.Label>Reviewed As</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Review Type"
                      value={myReview?.reviewedEmployee?.practice?.practiceName}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={12} sm={12}>
                  <Form.Group size="lg" className="mb-2">
                    <Form.Label>Team</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Team"
                      value={myReview.project === null ? "-" : myReview?.project?.projectName}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={12} sm={12}>
                  <Form.Group size="lg" className="mb-2">
                    <Form.Label>Review Type</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Review Type"
                      value={myReview?.reviewType?.reviewTypeName}
                      readOnly
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </div>
        )}
      </ReviewQuestionContext.Provider>
    </div>
  );
}